import type { BoxProps } from '@packages/shared';
import { Box, Typography, Image, LinkButton, DynamicYield } from '@packages/shared';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Premiumlounge32 } from '@packages/themes/icons';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AddToBasketButton } from '@packages/modules/src/AddToBasketButton/index';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer';
import { useQuery } from 'urql';
import { getProductAndFallbackId } from '@packages/modules/src/helpers/getProductAndFallbackId/getProductAndFallbackId';
import { PremiumBannerDataDocument } from '@packages/gql/generated/shopping/PremiumBannerDataDocument';
import { useEventTracking } from '../Tracking/useEventTracking';
import type { EventCategory } from '../Tracking/useEventTracking';

export interface PremiumBannerProps extends BoxProps {
  articleNumber?: string;
  mode: 'Basket' | 'CheckAndOrder';
}
const messages = defineMessages({
  customFlexiButtonText: {
    id: 'PremiumBanner.Flexikonto.Button.Text',
    defaultMessage: 'Mitglied werden',
  },
  premiumBannerText: {
    id: 'PremiumBanner.Text',
    defaultMessage: 'Versandkosten-Flatrate 1 Jahr{break} oder 2 Jahre',
  },
  premiumBannerInfo: {
    id: 'PremiumBanner.Info',
    defaultMessage:
      'Jetzt Vorteilsclub-Mitglied werden und <strong>sofort alle Versand- & Speditionskosten sparen</strong> für nur 30€/Jahr.*',
  },
  premiumBannerInfoLegalText: {
    id: 'PremiumBanner.Info.LegalText',
    defaultMessage:
      '*Die Mitgliedschaft ist jederzeit kündbar und verlängert sich bei Nichtkündigung jährlich.',
  },
});

/* GraphQL */ `
  query PremiumBannerData(
    $productId: String!
    $fallbackId: String!
    $locale: String!
    $lang: String!
    $skipAplus: Boolean! = false
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      ...AddToBasketFragment
    }
  }
`;
export const PremiumBanner: React.FC<PremiumBannerProps> = ({ articleNumber, mode, ...props }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    order: {
      hasFlexikontoAdvantageClub,
      assets: {
        premium: { getPremiumIcon },
      },
    },
    product: {
      aplus: { enabled: aplusEnabled },
    },
    dynamicYieldEnabled,
  } = useConfig();
  const { locale, language } = useI18n();
  const { formatMessage } = useIntl();
  const dispatch = useEventTracking();
  const [{ data, fetching }] = useQuery({
    query: PremiumBannerDataDocument,
    variables: {
      ...getProductAndFallbackId(`${articleNumber}-0`, undefined),
      locale,
      lang: language,
      skipAplus: !aplusEnabled,
    },
    context: useMemo(() => ({ suspense: false }), []),
    // this will only be called if flexikonto is enabled
    pause: !hasFlexikontoAdvantageClub,
  });
  useEffect(() => {
    dispatch(`${mode.toLocaleLowerCase()}_LoyalityProgram` as EventCategory, 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const clickTrackingHandler = () => {
    dispatch(`${mode.toLocaleLowerCase()}_LoyalityProgram` as EventCategory, 'click');
  };

  // CheckAndOrder step is not implemented yet so we return null
  if (!articleNumber || mode === 'CheckAndOrder') {
    return null;
  }
  const shouldShowPremiumIcon = getPremiumIcon && !hasFlexikontoAdvantageClub;
  const shouldShowDefaultPremiumIcon = !getPremiumIcon && !hasFlexikontoAdvantageClub;
  return (
    <>
      {dynamicYieldEnabled && mode === 'Basket' && <DynamicYield dataDyId="dy_basket_premium" />}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: !hasFlexikontoAdvantageClub ? 'row' : 'column',
          gap: 2,
        }}
        {...props}
      >
        {shouldShowPremiumIcon && (
          <Box
            sx={{
              width: '2.5rem',
              mr: 1,
            }}
          >
            <Image src={getPremiumIcon({ language })} alt="premium icon" width={40} height={40} />
          </Box>
        )}
        {shouldShowDefaultPremiumIcon && (
          <Box
            sx={{
              width: '1.75rem',
              height: '1.75rem',
              bgcolor: 'secondary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              mr: 2,
            }}
          >
            <Premiumlounge32 sx={{ width: '1.25rem' }} />
          </Box>
        )}
        {!hasFlexikontoAdvantageClub && (
          <LinkButton
            onClick={() => {
              setIsModalOpen(true);
              clickTrackingHandler();
            }}
          >
            <Typography variant="body2">
              {formatMessage(messages.premiumBannerText, { break: <br /> })}
            </Typography>
          </LinkButton>
        )}
        {hasFlexikontoAdvantageClub && (
          <>
            <Typography variant="body3" fontSize="0.75rem">
              {formatMessage(messages.premiumBannerInfo, {
                strong: (...chunks: ReactNode[]) => <strong>{chunks}</strong>,
              })}
            </Typography>
            {data && !fetching && (
              <AddToBasketButton
                onClick={clickTrackingHandler}
                data={{ ...data.product }}
                fullWidth={false}
                color="inherit"
                size="medium"
                sx={{ width: 'auto', backgroundColor: 'grey.main', color: 'grey.contrastText' }}
                customButtonText={messages.customFlexiButtonText}
              />
            )}
            <Typography variant="body3">
              {formatMessage(messages.premiumBannerInfoLegalText)}
            </Typography>
          </>
        )}
      </Box>
      <MiniDVLayer
        open={isModalOpen}
        sku={articleNumber}
        onClose={() => setIsModalOpen(false)}
        quantity={1}
        akl=""
      />
    </>
  );
};
